import React, { useEffect, useState } from "react";
import { Form, Button, Spin, Select, AutoComplete } from "antd";
import { ReactSVG } from "react-svg";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { each } from "lodash";
import { useSelector } from "react-redux";
import { getTranslation } from "../../../utils/helper";

import "./InviteUser.scss";
import { doFindEmails } from "../../../modules/user/api";

const InviteUserForm = ({
  form,
  isFetching,
  group,
  groups,
  invite,
  doGetGroupInviteEmails,
  doUpdateGroupInviteEmail,
  doUpdateGroupDisInviteEmail,
  doOffGroupInviteEmails,
}) => {
  const user = useSelector(state => state.auth.user);
  const setting = user.company;

  const [options, setOptions] = useState([]);
  const [copied, setCopied] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const inviteUrl = invite?.code ? `${process.env.REACT_APP_URI}/groups/invite?code=${invite.code}` : null;

  useEffect(() => {
    if (group) {
      setCurrentGroup(group);
    }
  }, [group]);

  useEffect(() => {
    if (groups?.length > 0) {
      setCurrentGroup(groups[0]);
    }
  }, [groups]);

  useEffect(() => {
    if (currentGroup) {
      form.setFieldsValue({
        group_id: currentGroup._id,
      });
      doGetGroupInviteEmails(currentGroup._id);
    }
  }, [currentGroup]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCopied(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleOffInviteEmails = async () => {
    await doOffGroupInviteEmails({
      id: currentGroup._id,
      inviteStatus: !invite.status,
    });
  };

  const handleOnFinish = async values => {
    await doUpdateGroupInviteEmail({
      id: currentGroup._id,
      email: values.email,
    });
    doGetGroupInviteEmails(currentGroup._id);
    form.setFieldsValue({
      email: null,
    });
  };

  const handleChangeGroup = value => {
    doGetGroupInviteEmails(value);
    setCurrentGroup(groups.find(group => group._id === value));
  };

  const onSearch = async searchText => {
    if (searchText) {
      const response = await doFindEmails({ email: searchText });
      const emailsData = response?.response?.emails || [];
      const optionsT = [];
      each(emailsData, email => {
        optionsT.push({ value: email });
      });
      setOptions(optionsT);
    }
  };

  const handleRemove = async user => {
    await doUpdateGroupDisInviteEmail({
      id: currentGroup._id,
      email: user.email,
    });
    doGetGroupInviteEmails(currentGroup._id);
  };

  return (
    <Spin spinning={isFetching}>
      <div className="InviteUserForm">
        <div className="FormHeader">
          <div className="left">
            {group
              ? `${getTranslation(setting, "User Groups", "Invite user to")} ${group.name}`
              : getTranslation(setting, "User Groups", "Invite User")}
          </div>
        </div>
        <div className="FormContent">
          <Form
            form={form}
            scrollToFirstError
            layout="vertical"
            hideRequiredMark
            colon={false}
            onFinish={handleOnFinish}>
            {groups && (
              <Form.Item label={getTranslation(setting, "User Groups", "Group")} name="group_id">
                <Select
                  size="large"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleChangeGroup}
                  placeholder={getTranslation(setting, "User Groups", "Group")}>
                  {groups.map(c => (
                    <Select.Option key={c._id} value={c._id}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            <div className="InviteEmails">
              <div className="Left">
                <Form.Item
                  label={getTranslation(setting, "User Groups", "Invite Users to Group")}
                  name="email"
                  rules={[
                    { type: "email", message: "Invalid Email" },
                    { required: true, message: "Required" },
                  ]}>
                  <AutoComplete
                    size="large"
                    options={options}
                    onSearch={onSearch}
                    placeholder={getTranslation(setting, "User Groups", "User Email Address")}
                  />
                </Form.Item>
              </div>
              <div className="Right">
                <Button size="large" htmlType="submit">
                  {getTranslation(setting, "User Groups", "Add")}
                </Button>
              </div>
            </div>
          </Form>
          <div className="InviteViaLink">
            <div className="InviteTitle">
              <div className="Left">
                <div className="Title">{getTranslation(setting, "User Groups", "Invite Via Link")}</div>
                {invite.status && (
                  <div className="SubTitle">
                    {getTranslation(setting, "User Groups", "Anyone with the link can join as user")}
                  </div>
                )}
              </div>
              <div className="Right">
                <Button type="link" onClick={() => handleOffInviteEmails()}>
                  {invite.status
                    ? getTranslation(setting, "User Groups", "Turn Off Link")
                    : getTranslation(setting, "User Groups", "Turn On Link")}
                  <ReactSVG className="icon" src="/icons/link-off.svg" />
                </Button>
              </div>
            </div>
            {invite.status && (
              <div className="InviteContent">
                <div className="Left">{inviteUrl}</div>
                <div className="Right">
                  <CopyToClipboard text={inviteUrl} onCopy={() => setCopied(true)}>
                    <Button size="large">{copied ? "Copied" : "Copy"}</Button>
                  </CopyToClipboard>
                </div>
              </div>
            )}
          </div>
          <div className="ManageUsers">
            <div className="Title">{getTranslation(setting, "User Groups", "Manage Users")}</div>
            <div className="UserList">
              <table>
                <tbody>
                  {invite?.inviteUsers?.map((user, index) => (
                    <tr key={index} className="User">
                      <td>
                        <div className="Email">{user?.email}</div>
                      </td>
                      <td>
                        <div className="Name">{user?.name}</div>
                      </td>
                      <td>
                        <div className="Status Pending">{getTranslation(setting, "User Groups", "Invite Pending")}</div>
                      </td>
                      <td>
                        <Button type="text" onClick={() => handleRemove(user)}>
                          <ReactSVG className="icon" src="/icons/remove.svg" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {invite?.users?.map((user, index) => (
                    <tr key={index} className="User">
                      <td>
                        <div className="Email">{user?.email}</div>
                      </td>
                      <td>
                        <div className="Name">{user?.name}</div>
                      </td>
                      <td>
                        <div className="Status Accept">{getTranslation(setting, "User Groups", "Accepted")}</div>
                      </td>
                      <td>
                        <Button type="text" onClick={() => handleRemove(user)}>
                          <ReactSVG className="icon" src="/icons/remove.svg" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default InviteUserForm;
